import { useRef, useState } from "react";
import {
  Button,
  ButtonGroup,
  Checkbox,
  Column,
  Dialog,
  FormField,
  Row,
  Text,
  useToast,
} from "@hightouch/htui";
import {
  type WebPagePsdExportRunQuery,
  WebPagePsdExportRunStatus,
  useExportWebPageToPsdMutation,
  useWebPagePsdExportRunQuery,
} from "src/graphql";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import { captureException } from "src/lib/monitoring";
import type { WebPageContent } from "src/pages/ad-studio/components/web-page-preview";
import type { LayeredFormat } from "src/pages/ad-studio/psd-export/build-email-layered";
import { useDownloadWebPage } from "src/pages/ad-studio/files/use-download-web-page";
import {
  WEB_PAGE_DEVICES,
  webPageLayerFrames,
} from "src/pages/ad-studio/files/web-page-layered-export";
import {
  PSD_EXPORT_TIMEOUT_MS,
  downloadLayerFiles,
  downloadedTitle,
  formatApps,
  formatList,
  isLayeredFormat,
} from "src/pages/lifecycle-studio/emails/png-export/export-email";
import {
  WEB_PAGE_FORMAT_OPTIONS,
  type WebPageFormat,
} from "src/pages/projects/library/files/export-formats";
import { FormatCheckboxes } from "src/pages/projects/library/files/format-checkboxes";

type Run = NonNullable<WebPagePsdExportRunQuery["webPagePsdExportRun"]>;

/** Downloads a web page as a zip of its HTML and images, as layered PSDs or IDMLs, or any mix of them. */
export function WebPageDownloadDialog({
  fileId,
  name,
  content,
  onClose,
}: {
  fileId: string;
  name: string;
  content: WebPageContent;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [formats, setFormats] = useState<Set<WebPageFormat>>(
    () => new Set(["html"]),
  );
  const [widths, setWidths] = useState(
    () => new Set(WEB_PAGE_DEVICES.map((device) => device.width)),
  );
  // Holds the formats chosen at Download, so toggling a checkbox mid-export can't change what's delivered.
  const [pending, setPending] = useState<{
    runId: string;
    formats: LayeredFormat[];
  } | null>(null);
  const runStartedAt = useRef(0);
  const zip = useDownloadWebPage();
  const exportPsd = useExportWebPageToPsdMutation();
  const { refetchInterval, resetBackoff } = useGovernedRefetchInterval<
    WebPagePsdExportRunQuery | undefined
  >({
    baseMs: 1500,
    maxMs: 8000,
    compareKey: (data) => [data?.webPagePsdExportRun?.status],
  });
  const [assembling, setAssembling] = useState<LayeredFormat[] | null>(null);

  const layered = [...formats].filter(isLayeredFormat);

  const fail = (error: unknown, failedFormats: LayeredFormat[]) => {
    captureException(error);
    setPending(null);
    toast({
      id: `web-page-${failedFormats.join("-")}`,
      title: `Couldn't export ${formatList(failedFormats)}`,
      message: error instanceof Error ? error.message : undefined,
      variant: "error",
    });
  };

  const deliver = async (completed: Run, deliveredFormats: LayeredFormat[]) => {
    setAssembling(deliveredFormats);
    try {
      const count = await downloadLayerFiles(
        webPageLayerFrames(completed.frames),
        name,
        deliveredFormats,
      );
      toast({
        id: `web-page-${deliveredFormats.join("-")}`,
        title: downloadedTitle(deliveredFormats, count),
        variant: "success",
      });
      onClose();
    } catch (error) {
      fail(error, deliveredFormats);
    } finally {
      setAssembling(null);
    }
  };

  useWebPagePsdExportRunQuery(
    { id: pending?.runId ?? "" },
    {
      enabled: pending !== null,
      refetchInterval,
      onSuccess: ({ webPagePsdExportRun: run }) => {
        if (!pending) return;
        // A null run means the page was deleted or made private mid-export.
        if (!run) {
          fail(new Error("Export is no longer available"), pending.formats);
        } else if (run.status === WebPagePsdExportRunStatus.Complete) {
          setPending(null);
          void deliver(run, pending.formats);
        } else if (run.status === WebPagePsdExportRunStatus.Failed) {
          fail(new Error(run.error ?? "Export failed"), pending.formats);
        } else if (Date.now() - runStartedAt.current > PSD_EXPORT_TIMEOUT_MS) {
          fail(new Error("Export timed out"), pending.formats);
        }
      },
      onError: (error) => {
        if (pending) fail(error, pending.formats);
      },
    },
  );

  const confirm = async () => {
    if (layered.length === 0) {
      if (formats.has("html")) await zip.download(name, content);
      onClose();
      return;
    }
    try {
      // The server render takes minutes, so it starts alongside the HTML zip (which reports its own errors).
      const [result] = await Promise.all([
        exportPsd.mutateAsync({ fileId, widths: [...widths] }),
        formats.has("html") ? zip.download(name, content) : undefined,
      ]);
      resetBackoff();
      runStartedAt.current = Date.now();
      setPending({ runId: result.exportWebPageToPsd.runId, formats: layered });
    } catch (error) {
      fail(error, layered);
    }
  };

  const exporting = pending?.formats ?? assembling;
  const isWorking =
    zip.isDownloading || exportPsd.isLoading || exporting !== null;

  return (
    <Dialog
      isOpen
      variant="form"
      width="md"
      title="Download web page"
      onClose={onClose}
      actions={
        <ButtonGroup>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            isLoading={isWorking}
            isDisabled={
              formats.size === 0 || (layered.length > 0 && widths.size === 0)
            }
            onClick={() => void confirm()}
          >
            Download
          </Button>
        </ButtonGroup>
      }
    >
      <Column gap={5}>
        <FormatCheckboxes
          options={WEB_PAGE_FORMAT_OPTIONS}
          value={formats}
          onChange={setFormats}
        />
        {layered.length > 0 && (
          <FormField label="Device">
            <Row gap={4}>
              {WEB_PAGE_DEVICES.map((device) => (
                <Checkbox
                  key={device.width}
                  label={device.label}
                  isChecked={widths.has(device.width)}
                  onChange={(event) =>
                    setWidths((previous) => {
                      const next = new Set(previous);
                      if (event.target.checked) next.add(device.width);
                      else next.delete(device.width);
                      return next;
                    })
                  }
                />
              ))}
            </Row>
          </FormField>
        )}
        {(exporting || layered.length > 0) && (
          <Text size="sm" color="text.secondary">
            {exporting
              ? `Preparing ${formatList(exporting)} download… This can take a few minutes. Keep this dialog open.`
              : `Text exports as editable layers when its font can be identified. Images and complex effects remain pixel layers in ${formatApps(layered)}.`}
          </Text>
        )}
      </Column>
    </Dialog>
  );
}
